import React, { useState } from "react";
import { Phone, Mail, Clock, MapPin, ArrowRight } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";
import { brand } from "../mock";
import { trackEvent } from "./Analytics";
import { submitLead } from "../lib/api";

const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please complete the required fields.");
      return;
    }
    const result = await submitLead({ source: "contact", ...form });
    if (result.ok) {
      toast.success("Message sent — we’ll respond shortly.");
    } else {
      toast.success("Got your message — we’ll respond shortly.");
    }
    trackEvent("lead_submit", { source: "contact_form" });
    setForm({ name: "", email: "", phone: "", message: "" });
  };

  return (
    <section id="contact" className="relative py-20 lg:py-28 bg-[#faf3eb]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12 lg:gap-16">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#daae9c]">
            <span className="w-6 h-px bg-sterling-dark" />
            Get in Touch
          </div>
          <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl text-ink mt-4 leading-[1.05] text-balance">
            Ready to book or{" "}
            <span className="italic text-[#daae9c]">curious about pricing?</span>
          </h2>
          <p className="mt-5 text-ink/70 leading-relaxed">
            Reach out — we’ll handle the rest. Every quote is personalised
            and we respond within 24 hours.
          </p>

          <div className="mt-10 space-y-4">
            <a
              href={`tel:${brand.phone.replace(/\s/g, "")}`}
              className="flex items-center gap-4 p-5 bg-white border border-[#ead8c9] rounded-2xl hover:border-sterling-dark transition-colors group"
            >
              <div className="w-12 h-12 rounded-xl bg-[#faf3eb] group-hover:bg-sterling flex items-center justify-center transition-colors">
                <Phone size={20} className="text-[#daae9c]" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-ink/60">
                  Phone
                </div>
                <div className="text-ink font-medium">{brand.phone}</div>
              </div>
            </a>
            <a
              href={`mailto:${brand.email}`}
              className="flex items-center gap-4 p-5 bg-white border border-[#ead8c9] rounded-2xl hover:border-sterling-dark transition-colors group"
            >
              <div className="w-12 h-12 rounded-xl bg-[#faf3eb] group-hover:bg-sterling flex items-center justify-center transition-colors">
                <Mail size={20} className="text-[#daae9c]" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-ink/60">
                  Email
                </div>
                <div className="text-ink font-medium">{brand.email}</div>
              </div>
            </a>
            <div className="flex items-center gap-4 p-5 bg-white border border-[#ead8c9] rounded-2xl">
              <div className="w-12 h-12 rounded-xl bg-[#faf3eb] flex items-center justify-center">
                <Clock size={20} className="text-[#daae9c]" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-ink/60">
                  Working Hours
                </div>
                <div className="text-sm text-ink">{brand.hours.weekday}</div>
                <div className="text-sm text-ink">{brand.hours.saturday}</div>
                <div className="text-sm text-ink/60">{brand.hours.sunday}</div>
              </div>
            </div>
            <div className="flex items-center gap-4 p-5 bg-white border border-[#ead8c9] rounded-2xl">
              <div className="w-12 h-12 rounded-xl bg-[#faf3eb] flex items-center justify-center">
                <MapPin size={20} className="text-[#daae9c]" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-ink/60">
                  Service Area
                </div>
                <div className="text-ink">{brand.location}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="relative">
          <div className="absolute -inset-2 bg-sterling/20 rounded-3xl blur-xl -z-10" />
          <form
            onSubmit={onSubmit}
            className="bg-white border border-[#ead8c9] rounded-3xl p-8 lg:p-10 shadow-[0_20px_60px_-20px_rgba(138,93,72,0.2)]"
          >
            <h3 className="font-serif-display text-3xl text-ink">
              Send us a message
            </h3>
            <p className="text-sm text-ink/60 mt-2">
              Or use the personalised quote form for service requests.
            </p>

            <div className="mt-6 space-y-4">
              <div>
                <label className="text-xs text-ink/70 uppercase tracking-wider">
                  Name
                </label>
                <Input
                  value={form.name}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, name: e.target.value }))
                  }
                  className="mt-1.5 h-11 bg-white border-[#e8d9cc] focus-visible:ring-sterling rounded-lg"
                  placeholder="Jane Doe"
                />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-ink/70 uppercase tracking-wider">
                    Email
                  </label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, email: e.target.value }))
                    }
                    className="mt-1.5 h-11 bg-white border-[#e8d9cc] focus-visible:ring-sterling rounded-lg"
                    placeholder="you@email.com"
                  />
                </div>
                <div>
                  <label className="text-xs text-ink/70 uppercase tracking-wider">
                    Phone
                  </label>
                  <Input
                    value={form.phone}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, phone: e.target.value }))
                    }
                    className="mt-1.5 h-11 bg-white border-[#e8d9cc] focus-visible:ring-sterling rounded-lg"
                    placeholder="04xx xxx xxx"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs text-ink/70 uppercase tracking-wider">
                  Message
                </label>
                <Textarea
                  value={form.message}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, message: e.target.value }))
                  }
                  rows={5}
                  className="mt-1.5 bg-white border-[#e8d9cc] focus-visible:ring-sterling rounded-lg"
                  placeholder="How can we help?"
                />
              </div>
              <Button
                type="submit"
                className="w-full h-12 bg-[#daae9c] hover:bg-white text-ink border border-transparent hover:border-[#daae9c] transition-colors rounded-full"
              >
                Send message
                <ArrowRight size={16} className="ml-2" />
              </Button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
